import React from 'react';
import { AppBar, AppBarSection, AppBarSpacer } from '@progress/kendo-react-layout';
import SettingsButton from './SettingsButton';
import { NotificationBellButton } from './notificationCenter/NotificationBellButton';
import { IndexDropdown } from '../grid/toolbar/IndexDropdown';
import { useCpsDialog } from '../common/useCpsDialog';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { useGlobalizationContext, useTranslation, useViewsContext } from '@cs-cms/state-management';
import { CpsSvgIcon } from '../shared/CpsSvgIcon';
import { GlobalSearchFilter } from '../grid/toolbar/globalFilters/GlobalSearchFilter';
import { useUIConfig } from '../UiProvider';
import { RoutePath } from '../types/RoutePath';

interface TopBarProps {
  maintainancePagePreview?: boolean;
}

const TopBar: React.FC<TopBarProps> = ({ maintainancePagePreview }) => {
  const { pathname } = useLocation();
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);
  const isSettingsPath = matchPath(`${RoutePath.SETTINGS}/*`, pathname);
  const isHomePath = matchPath(`${RoutePath.HOME}`, pathname);
  const isAccessDeniedPath = matchPath(`${RoutePath.ACCESS_DENIED}`, pathname);
  const isMaintenancePath = matchPath(`${RoutePath.MAINTENANCE}`, pathname);
  const navigate = useNavigate();
  const { Logo, environment } = useUIConfig();

  const { hasUnsavedChanges, setHasUnsavedChanges } = useViewsContext();
  const { showDialog, DialogComponent } = useCpsDialog();

  const handleBackToHome = () => {
    if (hasUnsavedChanges) {
      showDialog({
        title: translate('custom.settings.views.private.unsavedChangesTitle'),
        message: translate('custom.settings.views.private.unsavedChangesMessage'),
        confirmButtonLabel: translate('custom.common.discard'),
        discardButtonLabel: translate('custom.common.cancel'),
        onConfirm: confirmed => {
          if (confirmed) {
            setHasUnsavedChanges?.(false);
            navigate(RoutePath.HOME);
          }
        }
      });
    } else {
      navigate(RoutePath.HOME);
    }
  };

  return (
    <AppBar className="topbar">
      {isSettingsPath && !maintainancePagePreview ? (
        <>
          <AppBarSection>
            <CpsSvgIcon customIcon="backPageIcon" className="settingsBackHome" onClick={handleBackToHome} />
          </AppBarSection>
          <AppBarSection className="settingsTitle">
            <h1>{translate('custom.settings.title')}</h1>
          </AppBarSection>
        </>
      ) : (
        <>
          <AppBarSection>
            {Logo && (
              <Logo
                className="cpsLogo"
                onClick={() => (!maintainancePagePreview ? window.location.reload() : undefined)}
              />
            )}
            {environment && environment !== 'PROD' && <span className="environmentLabel">{environment}</span>}
          </AppBarSection>
          {isHomePath && (
            <>
              <AppBarSpacer />
              <AppBarSection>
                <GlobalSearchFilter />
              </AppBarSection>
              <AppBarSpacer />
            </>
          )}
        </>
      )}
      {!isAccessDeniedPath && !isMaintenancePath && !maintainancePagePreview && (
        <>
          <AppBarSection className="topBarRightSection">
            <IndexDropdown />
            <div className="topBarActionButtons">
              <NotificationBellButton />
              <SettingsButton />
            </div>
          </AppBarSection>
        </>
      )}
      {DialogComponent}
    </AppBar>
  );
};

export default TopBar;
